import { LevelRatingResultData_ExamSurveyQuestionFragment } from 'generated/graphql'
import QUESTION_SUBJECT_MAP from '@app.feature/exam/gsat2024-2/constants/questionSubjectMap'
import SUBJECT_MAX_QUESTION_COUNT from '@app.feature/exam/gsat2024-2/constants/subjectMaxQuestionCount'
import {
  LevelRatingTableProps,
  LevelRatingResultCardProps,
} from '@app.feature/exam/gsat2024-2/types/levelRatingType'

interface TableDataType extends LevelRatingTableProps {
  label: string
}

interface CardDataType extends LevelRatingResultCardProps {
  label: string
}

interface CardListDataType {
  label: string
  cardList: LevelRatingResultCardProps[]
}

export interface ParseSurveyResultDataType {
  question1: TableDataType
  question2: TableDataType
  question3: CardDataType
  question4: CardListDataType
  question5: CardDataType
  question6: CardListDataType
}

const parseSurveyResultData = (
  resultDatas: LevelRatingResultData_ExamSurveyQuestionFragment[],
): ParseSurveyResultDataType => {
  const result: ParseSurveyResultDataType = {
    question1: parseTableData(resultDatas[0]),
    question2: parseTableData(resultDatas[1]),
    question3: parseQuestion3Data(resultDatas[6]),
    question4: parseQuestion4Data([resultDatas[2], resultDatas[3]]),
    question5: parseQuestion5Data(resultDatas[4]),
    question6: parseQuestion6Data(resultDatas[5]),
  }

  return result
}

export default parseSurveyResultData

const parseTableData = (
  resultData: LevelRatingResultData_ExamSurveyQuestionFragment,
): TableDataType => {
  if (
    resultData?.answerStatistic.__typename ===
    'ExamSurveyAnswerTurnoutStatistic'
  ) {
    const tableDataList: TableDataType['tableDataList'] =
      resultData.answerStatistic.answerTurnouts.nodes.map((data) => ({
        dataText: `${data.turnout}%`,
        name: data.content,
      }))

    const tableData: TableDataType = {
      label: `${resultData.seq}. ${resultData.content}`,
      tableDataList,
    }

    return tableData
  } else {
    return {
      label: '',
      tableDataList: [],
    }
  }
}

const parseQuestion3Data = (
  resultData: LevelRatingResultData_ExamSurveyQuestionFragment,
): CardDataType => {
  if (
    resultData?.answerStatistic.__typename ===
    'ExamSurveyAnswerAverageSeqStatistic'
  ) {
    return {
      label: '3. GSAT 전체 체감 난이도',
      mainDataText: `${resultData.answerStatistic.score}`,
      subText: '/ 5',
      description: '1: 매우 쉬움 ~ 5: 매우 어려움',
    }
  } else {
    return {
      label: '',
      mainDataText: '',
    }
  }
}

const parseQuestion4Data = (
  resultDatas: LevelRatingResultData_ExamSurveyQuestionFragment[],
): CardListDataType => {
  const cardList: LevelRatingResultCardProps[] = resultDatas.map((data) => {
    if (
      data?.answerStatistic.__typename === 'ExamSurveyAnswerTurnoutStatistic'
    ) {
      const maxTurnout = data.answerStatistic.answerTurnouts.nodes.find(
        (node) => node.isMaxTurnout,
      )

      return {
        cardTitle: data.content.slice(0, 5),
        description: `(투표 참여자 중 ${maxTurnout?.turnout}%)`,
        mainDataText: maxTurnout?.content ?? '',
      }
    } else {
      return {
        mainDataText: '',
      }
    }
  })

  return {
    label: '4. GSAT 수리/추리 영역에서 가장 어려웠던 영역',
    cardList: cardList,
  }
}

const parseQuestion5Data = (
  resultData: LevelRatingResultData_ExamSurveyQuestionFragment,
): CardDataType => {
  if (
    resultData?.answerStatistic.__typename ===
    'ExamSurveyAnswerAverageWithoutLastSeqStatistic'
  ) {
    return {
      label: '5. 응시자 평균 과거 GSAT 응시 횟수',
      mainDataText: `${resultData.answerStatistic.score}`,
      description: '4회 이상 응시자 제외',
      subText: '회',
    }
  } else {
    return {
      label: '',
      mainDataText: '',
    }
  }
}

const parseQuestion6Data = (
  resultData: LevelRatingResultData_ExamSurveyQuestionFragment,
): CardListDataType => {
  if (
    resultData?.answerStatistic.__typename ===
    'ExamSurveyAnswerAverageNumericStatistic'
  ) {
    const cardList: LevelRatingResultCardProps[] =
      resultData?.answerStatistic.answerAverageNumerics.nodes.map((data) => {
        return {
          cardTitle: data.content,
          mainDataText: `${(
            SUBJECT_MAX_QUESTION_COUNT[QUESTION_SUBJECT_MAP[data.seq]] -
            data.averageNumeric
          ).toFixed(1)}`,
          subText: `/ ${
            SUBJECT_MAX_QUESTION_COUNT[QUESTION_SUBJECT_MAP[data.seq]]
          }`,
        }
      })

    return {
      label: '6. GSAT 평균 푼 문제 개수',
      cardList,
    }
  } else {
    return {
      label: '',
      cardList: [],
    }
  }
}
